import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import SlabRating from '../components/SlabRating';
import { SellerBadges, SellerStatus } from '../components/SellerBadges';
import ImageUpload from '../components/ImageUpload';
import CardListing from '../components/CardListing';
import { MembershipBadge, EliteRequiredPopup } from '../components/MembershipBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, User, Star, Package, ShoppingBag, Calendar, Edit2, Check, X, Video, Radio, ExternalLink, Play, Award } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUtils';

// Helper to get rating display
const getRatingDisplay = (profile) => {
  const totalRatedTransactions = profile?.total_rated_transactions || 0;
  const sellerRating = profile?.seller_rating;
  
  // If no rated transactions, show "New Seller"
  if (totalRatedTransactions === 0) {
    return {
      rating: null,
      displayText: "New Seller",
      showStars: false,
      reviewCount: 0
    };
  }
  
  // If has transactions but no rating (shouldn't happen), show "No Ratings Yet"
  if (sellerRating === null || sellerRating === undefined) {
    return {
      rating: null,
      displayText: "No Ratings Yet",
      showStars: false,
      reviewCount: totalRatedTransactions
    };
  }
  
  // Has actual rating
  return {
    rating: sellerRating,
    displayText: sellerRating.toFixed(1),
    showStars: true,
    reviewCount: totalRatedTransactions
  };
};

export const ProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user: currentUser, refreshUser } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', bio: '', profile_picture: '', live_stream_url: '' });
  const [showElitePopup, setShowElitePopup] = useState(false);
  
  // Live Stream states
  const [embedHtml, setEmbedHtml] = useState('');
  const [streamLoading, setStreamLoading] = useState(false);
  const [sellerListings, setSellerListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);

  const isOwnProfile = currentUser?.id === id;
  const isElite = currentUser?.membership_status === 'elite' || currentUser?.can_host_live_auctions;

  useEffect(() => {
    fetchProfile();
  }, [id]);

  // Fetch embed when profile has live_stream_url
  useEffect(() => {
    if (profile?.live_stream_url && profile?.is_live) {
      fetchEmbed(profile.live_stream_url);
      fetchSellerListings();
    }
  }, [profile?.live_stream_url, profile?.is_live]);

  const fetchProfile = async () => {
    try {
      const res = await api().get(`/users/${id}`);
      setProfile(res.data);
      setEditForm({
        name: res.data.name || '',
        bio: res.data.bio || '',
        profile_picture: res.data.profile_picture || '',
        live_stream_url: res.data.live_stream_url || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Profile not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmbed = async (url) => {
    if (!url) return;
    setStreamLoading(true);
    try {
      const res = await api().post('/live-stream/embed', { url });
      setEmbedHtml(res.data.embed_html);
    } catch (error) {
      console.error('Failed to get embed:', error);
    } finally {
      setStreamLoading(false);
    }
  };

  const fetchSellerListings = async () => {
    setListingsLoading(true);
    try {
      const res = await api().get(`/cards?seller_id=${id}&limit=6`);
      setSellerListings(res.data);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setListingsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api().put('/users/profile', editForm);
      toast.success('Profile updated');
      setEditing(false);
      fetchProfile();
      if (isOwnProfile) {
        refreshUser();
      }
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const toggleLiveStatus = async () => {
    // Check if user is Elite tier
    if (!isElite) {
      setShowElitePopup(true);
      return;
    }
    
    if (!profile?.live_stream_url) {
      toast.error('Please add a live stream URL first');
      return;
    }
    try {
      const newStatus = !profile.is_live;
      await api().put('/users/live-stream', null, {
        params: { live_stream_url: profile.live_stream_url, is_live: newStatus }
      });
      setProfile(prev => ({ ...prev, is_live: newStatus }));
      toast.success(newStatus ? 'You are now LIVE!' : 'Stream ended');
    } catch (error) {
      // Check if it's an Elite-only feature error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('Elite')) {
        setShowElitePopup(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update live status');
      }
    }
  };

  if (loading) return <PageLoader />;
  if (!profile) return null;

  const reviews = profile.reviews || [];

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {/* LIVE STREAM SECTION - Shows at top when seller is live */}
        {profile.is_live && profile.live_stream_url && (
          <div className="mb-8" data-testid="live-stream-section">
            {/* Live Badge Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00BFFF]/20 border border-[#00BFFF]/50 animate-pulse">
                <Radio className="w-4 h-4 text-[#00BFFF]" />
                <span className="text-[#00BFFF] font-bold text-sm uppercase tracking-wide">LIVE NOW</span>
              </div>
              <span className="text-zinc-400">{profile.name} is streaming</span>
            </div>
            
            {/* Video Embed */}
            <div className="bg-zinc-900 rounded-xl overflow-hidden border border-zinc-700">
              {streamLoading ? (
                <div className="h-[400px] flex items-center justify-center">
                  <LoadingSpinner size="lg" />
                </div>
              ) : embedHtml ? (
                <div 
                  className="w-full"
                  dangerouslySetInnerHTML={{ __html: embedHtml }}
                />
              ) : (
                <div className="h-[400px] flex items-center justify-center">
                  <div className="text-center">
                    <Video className="w-12 h-12 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500">Loading stream...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Current Auction Items Tray */}
            {sellerListings.length > 0 && (
              <div className="mt-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-4">
                  <ShoppingBag className="w-5 h-5 text-[#00BFFF]" />
                  Current Auction Items
                  <span className="text-sm font-normal text-zinc-500">- Buy without stopping the stream</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {sellerListings.map((card) => (
                    <Link 
                      key={card.id} 
                      to={`/cards/${card.id}`}
                      target="_blank"
                      className="bg-zinc-900/50 border border-zinc-700 rounded-lg p-3 hover:border-[#00BFFF]/50 transition-colors group"
                    >
                      <div className="aspect-[3/4] bg-zinc-800 rounded mb-2 overflow-hidden">
                        <img 
                          src={card.images?.[0] || card.image_url || 'https://placehold.co/150x200/1a1a1a/666?text=Card'} 
                          alt={card.player_name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <p className="text-white text-sm font-medium truncate">{card.player_name}</p>
                      <p className="text-[#00BFFF] font-bold">${card.price}</p>
                      <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" className="w-full text-xs bg-[#00BFFF] text-black hover:bg-[#00BFFF]/80">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View
                        </Button>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile Header */}
        <div className="stat-card p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Profile Picture with Live Badge */}
            <div className="relative flex flex-col items-center">
              {profile.profile_picture ? (
                <img 
                  src={resolveImageUrl(profile.profile_picture)} 
                  alt={profile.name}
                  className={`w-24 h-24 rounded-full object-cover border-2 ${
                    profile.is_live ? 'border-[#00BFFF] ring-4 ring-[#00BFFF]/30' : 'border-blue-500/30'
                  }`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<div class="w-24 h-24 rounded-full bg-blue-600/20 border-2 border-blue-500/30 flex items-center justify-center"><svg class="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>`;
                  }}
                />
              ) : (
                <div className={`w-24 h-24 rounded-full bg-blue-600/20 border-2 flex items-center justify-center ${
                  profile.is_live ? 'border-[#00BFFF] ring-4 ring-[#00BFFF]/30' : 'border-blue-500/30'
                }`}>
                  <User className="w-12 h-12 text-blue-400" />
                </div>
              )}
              {/* LIVE Badge */}
              {profile.is_live && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider animate-pulse"
                  style={{ backgroundColor: '#00BFFF', color: '#000' }}
                >
                  LIVE
                </div>
              )}
              {profile.membership_status === 'Creator' && !profile.is_live && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                  <Star className="w-3 h-3 text-black fill-black" />
                </div>
              )}
              {/* Founder Badge below profile picture */}
              {profile.is_founder && profile.founder_badge_url && (
                <img 
                  src={profile.founder_badge_url} 
                  alt="Founder Elite" 
                  className="w-20 h-auto object-contain mt-3" 
                  title="Founder Elite - First 25 Elite Members"
                  data-testid="founder-badge"
                  style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))' }}
                />
              )}
              {profile.is_founder_gold && profile.founder_gold_badge_url && (
                <img 
                  src={profile.founder_gold_badge_url} 
                  alt="Founder Gold" 
                  className="w-20 h-auto object-contain mt-3" 
                  title="Founder Gold - First 25 Gold Members"
                  data-testid="founder-gold-badge"
                  style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))' }}
                />
              )}
            </div>
            
            <div className="flex-1 text-center sm:text-left">
              {editing ? (
                <div className="space-y-3 max-w-md">
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    placeholder="Display Name"
                    className="input-field"
                  />
                  
                  {/* Profile Picture Upload */}
                  <div className="mt-4">
                    <label className="block text-sm text-zinc-400 mb-2">Profile Picture</label>
                    <ImageUpload
                      uploadType="profile"
                      currentImage={editForm.profile_picture}
                      onUploadComplete={(url) => setEditForm({...editForm, profile_picture: url})}
                    />
                  </div>
                  
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm({...editForm, bio: e.target.value.slice(0, 250)})}
                    placeholder="Write a short bio (250 chars max)"
                    maxLength={250}
                    rows={3}
                    className="w-full px-4 py-3 bg-zinc-900/50 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500/50"
                  />
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    {editForm.bio.length}/250 characters
                  </div>
                  
                  {/* Live Stream URL */}
                  <div className="mt-4">
                    <label className="block text-sm text-zinc-400 mb-2 flex items-center gap-2">
                      <Video className="w-4 h-4 text-[#00BFFF]" />
                      Live Stream URL (YouTube or Twitch)
                    </label>
                    <Input
                      value={editForm.live_stream_url}
                      onChange={(e) => setEditForm({...editForm, live_stream_url: e.target.value})}
                      placeholder="https://youtube.com/live/... or https://twitch.tv/..."
                      className="input-field"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={handleSaveProfile} className="btn-primary" size="sm">
                      <Check className="w-4 h-4 mr-1" /> Save
                    </Button>
                    <Button onClick={() => setEditing(false)} variant="outline" size="sm" className="border-zinc-700">
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 justify-center sm:justify-start">
                    <h1 className="font-heading font-bold text-3xl uppercase text-white">
                      {profile.name}
                    </h1>
                    {isOwnProfile && (
                      <button 
                        onClick={() => setEditing(true)}
                        className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                        data-testid="edit-profile-btn"
                      >
                        <Edit2 className="w-4 h-4 text-zinc-400" />
                      </button>
                    )}
                  </div>
                  <p className="text-zinc-400 mt-1">@{profile.username}</p>
                  
                  {/* Bio */}
                  {profile.bio && (
                    <p className="text-zinc-300 mt-3 max-w-lg">{profile.bio}</p>
                  )}
                  
                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                    {profile.is_live && (
                      <span className="badge flex items-center gap-1 animate-pulse" style={{ backgroundColor: 'rgba(0,191,255,0.2)', color: '#00BFFF', borderColor: '#00BFFF' }}>
                        <Radio className="w-3 h-3" />
                        LIVE
                      </span>
                    )}
                    
                    {/* Membership Badge - Gold or Elite */}
                    <MembershipBadge membershipStatus={profile.membership_status} size="md" />
                    
                    {/* Legacy Creator badge */}
                    {profile.membership_status === 'Creator' && (
                      <span className="badge badge-amber">Creator</span>
                    )}
                    
                    {/* Seller Badges */}
                    <SellerBadges 
                      rating={profile.rating || 0} 
                      totalSales={profile.total_sales || 0} 
                      size="md" 
                    />
                  </div>
                  
                  {/* Go Live Button (for own profile) - requires seller access */}
                  {isOwnProfile && (profile.subscription_active || profile.membership_status === 'gold' || profile.membership_status === 'elite' || profile.membership_status === 'Creator' || profile.is_seller) && (
                    <div className="mt-4">
                      <Button
                        onClick={toggleLiveStatus}
                        className={`${profile.is_live ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        style={!profile.is_live ? { backgroundColor: '#00BFFF', color: '#000' } : {}}
                        data-testid="toggle-live-btn"
                      >
                        {profile.is_live ? (
                          <>
                            <X className="w-4 h-4 mr-2" />
                            End Stream
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Go Live
                          </>
                        )}
                      </Button>
                      {!profile.live_stream_url && (
                        <p className="text-xs text-zinc-500 mt-1">Add a stream URL in your profile first</p>
                      )}
                    </div>
                  )}
                </>
              )}
              
              {/* Stats */}
              {!editing && (
                <div className="flex flex-wrap justify-center sm:justify-start gap-6 mt-6">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const ratingInfo = getRatingDisplay(profile);
                      if (ratingInfo.showStars) {
                        return (
                          <>
                            <SlabRating rating={ratingInfo.rating || 0} readonly size="sm" />
                            <span className={`font-bold ${
                              ratingInfo.rating >= 4.5 ? 'text-amber-400' : 
                              ratingInfo.rating > 0 ? 'text-emerald-400' : 'text-zinc-400'
                            }`}>{ratingInfo.displayText}</span>
                            <span className="text-zinc-500">({ratingInfo.reviewCount} reviews)</span>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <Award className="w-5 h-5 text-zinc-500" />
                            <span className="text-zinc-400 font-medium">{ratingInfo.displayText}</span>
                          </>
                        );
                      }
                    })()}
                  </div>
                  
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Package className="w-5 h-5" />
                    <span>{profile.active_listings || 0} listings</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-zinc-400">
                    <ShoppingBag className="w-5 h-5" />
                    <span>{profile.total_sales || 0} sold</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <section>
          <h2 className="font-heading font-bold text-2xl text-white mb-6">
            Reviews ({reviews.length})
          </h2>
          
          {reviews.length === 0 ? (
            <div className="stat-card p-8 text-center">
              <Star className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No reviews yet</p>
              <p className="text-zinc-500 text-sm mt-2">Complete transactions to receive reviews</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="stat-card p-6" data-testid={`review-${review.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-medium text-white">{review.reviewer_name}</p>
                        <SlabRating rating={review.rating} readonly size="sm" />
                      </div>
                      {review.comment && (
                        <p className="text-zinc-400">{review.comment}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Calendar className="w-3 h-3" />
                      {new Date(review.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      
      {/* Elite Required Popup */}
      {showElitePopup && (
        <EliteRequiredPopup 
          feature="Hosting Live Shows"
          onUpgrade={() => navigate('/subscription?tier=elite')}
          onClose={() => setShowElitePopup(false)}
        />
      )}
    </div>
  );
};

export default ProfilePage;
